import { Message } from "primereact/message";
import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import AlertTriangleIcon from "@/assets/icons/limite-alert-triangle.svg?react";
import BriefcaseIcon from "@/assets/icons/limite-briefcase.svg?react";
import CalendarIcon from "@/assets/icons/limite-calendar.svg?react";
import FlagIcon from "@/assets/icons/limite-flag.svg?react";
import { Dialog, FormField } from "@/ds";
import { JUSTIFICATIVA_DIALOG } from "../../../../nova-obra.constants";
import { formatDataBr } from "../../../format-data";
import styles from "./justificativa-condicoes-dialog.module.scss";
import { JUSTIFICATIVA_CONDICOES_MAX } from "./justificativa-condicoes-dialog.constants";
import type {
  JustificativaCondicoesDialogProps,
  JustificativaCondicoesDialogViewModel,
} from "./types-justificativa-condicoes-dialog";

/** `**trecho**` vira negrito. */
function comNegrito(texto: string) {
  return texto
    .split("**")
    .map((parte, indice) =>
      indice % 2 === 1 ? <strong key={indice}>{parte}</strong> : <span key={indice}>{parte}</span>,
    );
}

/**
 * Modal "Justificar e criar obra" (HU 2549, Figma `11202:416154`): aberta ao Criar Obra só quando há
 * ponto pendente (limite de classe e/ou contrato). Uma única justificativa cobre todos os pontos (RN21).
 */
export function JustificativaCondicoesDialogView(
  props: JustificativaCondicoesDialogProps & JustificativaCondicoesDialogViewModel,
) {
  const {
    justificativa,
    alterarJustificativa,
    textos,
    erro,
    mostrarErro,
    contrato,
    limiteUnico,
    tabela,
    variante,
    confirmar,
    periodo,
    onAjustarDatas,
    onAdicionarTipo,
    onClose,
    diasDaObra,
    relacaoLimiteUnico,
    relacaoContrato,
    linhasLimites,
  } = props;

  const cartaoPeriodo = (
    <div className={styles.card} data-testid="justificativa-periodo">
      <span className={styles.cardLabel}>
        <CalendarIcon aria-hidden />
        {JUSTIFICATIVA_DIALOG.periodoObra}
      </span>
      <span className={styles.cardValor}>
        {formatDataBr(periodo.inicio)} → {formatDataBr(periodo.fim)}
      </span>
      <span className={styles.cardNota}>{JUSTIFICATIVA_DIALOG.dias(diasDaObra)}</span>
    </div>
  );

  const cartaoLimite =
    limiteUnico && !tabela ? (
      <div className={styles.card} data-testid="justificativa-limite">
        <span className={styles.cardLabel}>
          <FlagIcon aria-hidden />
          {JUSTIFICATIVA_DIALOG.limiteClasse}
        </span>
        <span className={styles.cardValor}>
          <span
            className={limiteUnico.relacao === "DURANTE_A_OBRA" ? styles.dataNeutra : styles.perigo}
          >
            {formatDataBr(limiteUnico.dataLimite)}
          </span>
          <Tag
            severity={limiteUnico.relacao === "DURANTE_A_OBRA" ? "warning" : "danger"}
            value={limiteUnico.descricao}
          />
        </span>
        <span
          className={`${styles.cardNota} ${limiteUnico.relacao === "DURANTE_A_OBRA" ? "" : styles.notaPerigo}`}
        >
          {relacaoLimiteUnico}
        </span>
      </div>
    ) : null;

  const cartaoContrato = contrato ? (
    <div className={styles.card} data-testid="justificativa-contrato">
      <span className={styles.cardLabel}>
        <BriefcaseIcon aria-hidden />
        {JUSTIFICATIVA_DIALOG.terminoContrato}
      </span>
      <span className={`${styles.cardValor} ${styles.contrato}`}>
        {formatDataBr(contrato.terminoContrato)}
      </span>
      <span className={`${styles.cardNota} ${styles.contrato}`}>{relacaoContrato}</span>
    </div>
  ) : null;

  return (
    <Dialog
      visible
      modal
      onHide={onClose}
      className={`${styles.dialog} ${styles[variante]}`}
      headerClassName={`${styles.header} ${textos.variante === "varios" ? styles.headerTopo : ""}`}
      contentClassName={styles.content}
      pt={{ footer: { className: styles.footerRoot } }}
      data-testid="justificativa-condicoes-dialog"
      aria-label={textos.titulo}
      header={
        <div className={styles.headerLeft}>
          <span className={styles.badge} aria-hidden>
            <AlertTriangleIcon />
          </span>
          <div className={styles.headerText}>
            <h2 className={styles.title}>{textos.titulo}</h2>
            <p className={styles.subtitle}>{JUSTIFICATIVA_DIALOG.subtitle}</p>
          </div>
        </div>
      }
      footer={
        <div className={styles.footer}>
          <Button
            type="button"
            text
            severity="secondary"
            className={styles.ajustar}
            label={JUSTIFICATIVA_DIALOG.ajustarDatas}
            onClick={onAjustarDatas}
          />
          {textos.podeAdicionarTipo && (
            <Button
              type="button"
              severity="secondary"
              outlined
              label={JUSTIFICATIVA_DIALOG.adicionarTipo}
              onClick={onAdicionarTipo}
            />
          )}
          <Button type="button" label={JUSTIFICATIVA_DIALOG.justificarCriar} onClick={confirmar} />
        </div>
      }
    >
      <div className={styles.body}>
        <p className={styles.descricao}>{comNegrito(textos.descricao)}</p>

        <div className={styles.cards}>
          {cartaoPeriodo}
          {cartaoLimite}
          {cartaoContrato}
        </div>

        {tabela && (
          <div className={styles.tabelaWrapper}>
            <table className={styles.tabela} data-testid="justificativa-tabela-limites">
              <thead>
                <tr>
                  <th>{JUSTIFICATIVA_DIALOG.vistoria}</th>
                  <th>{JUSTIFICATIVA_DIALOG.dataLimite}</th>
                  <th>{JUSTIFICATIVA_DIALOG.emRelacaoObra}</th>
                  <th>{JUSTIFICATIVA_DIALOG.renovaCom}</th>
                </tr>
              </thead>
              <tbody>
                {linhasLimites.map((limite) => (
                  <tr
                    key={limite.vistoria}
                    className={limite.critico ? styles.linhaCritica : undefined}
                  >
                    <th scope="row">{limite.descricao}</th>
                    <td className={limite.critico ? styles.perigo : undefined}>
                      {formatDataBr(limite.dataLimite)}
                    </td>
                    <td>
                      <Tag
                        severity={limite.critico ? "danger" : "warning"}
                        value={limite.relacao}
                        className={styles.tag}
                      />
                    </td>
                    <td>{limite.renovaCom ?? JUSTIFICATIVA_DIALOG.semTipo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Message
          severity={textos.perigo ? "error" : "warn"}
          className={classNames("p-message-block", styles.pergunta)}
          text={textos.pergunta}
        />
        <p className={styles.apoio}>{textos.apoio}</p>

        <FormField.Root className={styles.campo} error={mostrarErro ? erro : null}>
          <FormField.Label>
            {JUSTIFICATIVA_DIALOG.justificativa} {JUSTIFICATIVA_DIALOG.obrigatorio}
          </FormField.Label>
          <FormField.Control>
            <InputTextarea
              value={justificativa}
              maxLength={JUSTIFICATIVA_CONDICOES_MAX}
              rows={3}
              aria-required
              onChange={(event) => alterarJustificativa(event.target.value)}
              className={styles.textarea}
            />
          </FormField.Control>
          <FormField.Error />
        </FormField.Root>
      </div>
    </Dialog>
  );
}
